import * as models from 'pkg/api/models';
import { hasAllowedAction } from 'pkg/api/models/guards';
import type { User } from 'pkg/api/models/user';
import { Type } from 'pkg/api/models/user';
import { age } from 'pkg/api/models/user/fmt';
import { isValidBirthDate } from 'pkg/date';
import DateTime from 'pkg/datetime';
import { validateSwedishPersonalId } from 'pkg/strings';

export function is360PlayerAdmin(user: User): boolean {
	return user?.type === Type.TSAdmin;
}

export function isAnAdult(user: User): boolean {
	return age(user) >= 18;
}

export function hasHandicap(user: User): boolean {
	return user.flags.includes('has_handicap');
}

export function canReceiveFormInvite(user?: User): boolean {
	return (
		!!user && hasAllowedAction(user, models.Action.UserReceiveFormInvite)
	);
}

export function isMissingRequiredInformation(user: User): boolean {
	return user?.missingRequiredFields?.length > 0;
}

const luhnValid = (luhnNumber: number) => {
	return ((luhnNumber % 10) + checksum(Math.floor(luhnNumber / 10))) % 10 == 0;
};

const checksum = (number: number): number => {
	let luhn: number = 0;

	for (let i = 0; number > 0; i++) {
		let cur = number % 10;

		if (i % 2 === 0) {
			cur = cur * 2;
			if (cur > 9) {
				cur = (cur % 10) + Math.floor(cur / 10);
			}
		}

		luhn += cur;
		number = Math.floor(number / 10);
	}

	return luhn % 10;
};

export function validatePersonalNumber(personalNumber: string) {
	let passedPersonalNumberCheck = true;

	if (!validateSwedishPersonalId(personalNumber)) {
		passedPersonalNumberCheck = false;
	}

	const luhnNumber = Number.parseInt(
		personalNumber.slice(2, 8) + personalNumber.slice(9),
		10
	);
	if (luhnNumber == 0) {
		passedPersonalNumberCheck = false;
	}

	// only years in this range is valid in IdrottOnline
	const year = Number.parseInt(personalNumber.slice(0, 4), 10);
	if (year < 1800 || year > 2100) {
		passedPersonalNumberCheck = false;
	}

	if (!isValidBirthDate(personalNumber.slice(0, 8))) {
		passedPersonalNumberCheck = false;
	}

	if (!luhnValid(luhnNumber)) {
		passedPersonalNumberCheck = false;
	}

	return passedPersonalNumberCheck;
}

export function isBirthdayToday(user: User): boolean {
	if (!user.birthDate) return false;

	const today = new DateTime();
	const [, month, day] = user.birthDate
		.split('-')
		.map((str) => Number.parseInt(str, 10));

	return today.getDay() === day && today.getMonth() === month;
}

export function checkIfShouldValidatePersonalNumber(
	nationality: string,
	personalNumbervalue: string,
	isAdmin: boolean = false
) {
	let shouldValidatePersonalNumber = false;

	if (nationality === 'se') {
		if (!isAdmin) {
			// Always require personal number for players if nationality is SE
			shouldValidatePersonalNumber = true;
		} else if (isAdmin && personalNumbervalue !== '') {
			// Only require personal number for admins if value not empty
			// This is used when editing contacts
			shouldValidatePersonalNumber = true;
		}
	} else if (nationality !== 'se' && personalNumbervalue !== '') {
		shouldValidatePersonalNumber = true;
	}

	return shouldValidatePersonalNumber;
}
